import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Box, Typography, MenuItem } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { loadCountries, loadTimezones } from '@/shared/store/actions/DictionaryActionCreators';
import { useSelector } from 'react-redux';
import { UseAppDispatch } from '@/shared/store';
import { EditTextField, EditTextFieldRow, EditAutocomplete } from '@/shared/UI/styled/global';
import { CompanyGeneralSchema } from '@/shared/consts/schema';
import { GridInfoGeneral, GridInfoModelGeneral } from '@/shared/models/company.model';
import Loader from '@/shared/UI/elements/Loader';


const EditCompanyFormGeneral = forwardRef(({ data }: any, ref) => {
  const appDispatch = UseAppDispatch();
  const [model, setModel] = useState<GridInfoGeneral>(GridInfoModelGeneral());
  const dictionariesCountries = useSelector((state: any) => state.dictionaries.countries);
  const dictionariesTimezones = useSelector((state: any) => state.dictionaries.timezones);
  const [dataCountries, setDataCountries] = useState<string[]>([]);
  const [states, setStates] = useState<{ code: string; label: string }[]>([]);

  useEffect(() => {
    const countries = dictionariesCountries.map((country: any) => country.name);
    setDataCountries(countries);
  }, [dictionariesCountries]);

  useEffect(() => {
    appDispatch(loadCountries());
    appDispatch(loadTimezones());
  }, []);

  const {
    control,
    trigger,
    getValues,
    setValue,
    reset,
    formState: { isValid },
  } = useForm<GridInfoGeneral>({
    mode: 'onChange',
    resolver: yupResolver(CompanyGeneralSchema),
    defaultValues: model
  });

  useImperativeHandle(ref, () => ({
    async submit () {
      await trigger();
      if (isValid) {
        return Object.assign({}, getValues());
      }
    },
  }));

  const selectedCountry = useWatch({
    control,
    name: 'country',
  });

  useEffect(() => {
    if (data) {
      setModel(data);
      reset(data);
    }
  }, [data]);

  useEffect(() => {
    const countryData = dictionariesCountries?.find((item: any) => item.name === getValues('country'));
    const stateList = countryData?.states || [];
    if (stateList) {
      const formattedStates = stateList.map((state: any) => ({
        code: state.code,
        label: state.name,
      }));
      setStates(formattedStates);
    } else {
      setStates([]);
    }
  }, [getValues('country')]);

  if (!model) return <Loader show={true} />;

  return (
    <Box
      component="form"
      sx={{ width: '100%' }}>

      <Typography
        sx={{ marginBottom: '20px' }}
        variant='h4'>
        General Settings
      </Typography>

      <EditTextFieldRow>
        <Controller
          control={control}
          name="name"
          defaultValue={model.name}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="Name"
              placeholder="Name"
              error={error !== undefined}
              InputLabelProps={{ shrink: false }}
              helperText={error ? `${error.message}` : ' '}
              fullWidth
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="dotNumber"
          defaultValue={model.dotNumber}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="DOT Number"
              placeholder="DOT Number"
              error={error !== undefined}
              InputLabelProps={{ shrink: false }}
              helperText={error ? `${error.message}` : ' '}
              sx={{ maxWidth: '297px' }}
              fullWidth
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="mcNumber"
          defaultValue={model.mcNumber ?? ''}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="MC Number"
              placeholder="MC Number"
              error={error !== undefined}
              InputLabelProps={{ shrink: false }}
              helperText={error ? `${error.message}` : ' '}
              sx={{ maxWidth: '297px' }}
              fullWidth
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="timezone"
          defaultValue={model.timezone || 'UTC'}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              select
              label="Time Zone"
              error={!!error}
              sx={{ maxWidth: '297px' }}
              helperText={error ? error.message : ' '}
              fullWidth
              {...field}
            >
              {dictionariesTimezones.map((tz: any) => (
                <MenuItem key={tz.id} value={tz.name}>
                  {tz.label || tz.name}
                </MenuItem>
              ))}
            </EditTextField>
          )}
        />

      </EditTextFieldRow>

      <EditTextFieldRow>
        <Controller
          control={control}
          name="country"
          defaultValue={model.country || ''}
          rules={{ required: 'Country is required' }}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              select
              label="Country"
              error={!!error}
              helperText={error ? error.message : ' '}
              sx={{ maxWidth: '297px' }}
              fullWidth
              {...field}
              onChange={(e) => {
                field.onChange(e);
                setValue('state', '');
              }}
            >
              {dataCountries.map((c: any) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </EditTextField>
          )}
        />
        <Controller
          control={control}
          name="state"
          defaultValue={model.state || ''}
          rules={{ required: 'State is required' }}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              select
              label="State"
              error={!!error}
              helperText={error ? error.message : ' '}
              sx={{ maxWidth: '297px' }}
              fullWidth
              {...field}
              disabled={!selectedCountry}
            >
              {states.map((state: any) => (
                <MenuItem key={state.code} value={state.code}>
                  {state.label}
                </MenuItem>
              ))}
            </EditTextField>
          )}
        />
        <Controller
          control={control}
          name="city"
          defaultValue={model.city}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="City"
              placeholder="City"
              error={error !== undefined}
              helperText={error ? `${error.message}` : ' '}
              sx={{ maxWidth: '297px' }}
              InputLabelProps={{ shrink: false }}
              fullWidth
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          defaultValue={model.email ?? ''}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="Email"
              placeholder="Email"
              error={error !== undefined}
              InputLabelProps={{ shrink: false }}
              helperText={error ? `${error.message}` : ' '}
              sx={{ maxWidth: '297px' }}
              fullWidth
              {...field}
            />
          )}
        />
      </EditTextFieldRow>

      <EditTextFieldRow>
        <Controller
          control={control}
          name="street"
          defaultValue={model.street}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="Street"
              placeholder="Street"
              error={error !== undefined}
              sx={{ maxWidth: '297px' }}
              InputLabelProps={{ shrink: false }}
              fullWidth
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="zip"
          defaultValue={model.zip}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="ZIP"
              placeholder="ZIP"
              error={error !== undefined}
              sx={{ maxWidth: '297px' }}
              InputLabelProps={{ shrink: false }}
              fullWidth
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          defaultValue={model.phone ?? ''}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="Phone"
              placeholder="Phone"
              error={error !== undefined}
              sx={{ maxWidth: '297px' }}
              InputLabelProps={{ shrink: false }}
              fullWidth
              {...field}
            />
          )}
        />
        <Controller
          control={control}
          name="contactPhone"
          defaultValue={model.contactPhone ?? ''}
          render={({ field, fieldState: { error } }) => (
            <EditTextField
              type="text"
              label="Contact Phone"
              placeholder="Contact Phone"
              error={error !== undefined}
              sx={{ maxWidth: '297px' }}
              InputLabelProps={{ shrink: false }}
              fullWidth
              {...field}
            />
          )}
        />
      </EditTextFieldRow>
    </Box>
  );
});

EditCompanyFormGeneral.displayName = 'EditCompanyFormGeneral';

export default EditCompanyFormGeneral;
